import { useState, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, RotateCw } from 'lucide-react';
import './ImagePreviewModal.css';

const ImagePreviewModal = ({
  images = [],
  initialIndex = 0,
  isOpen,
  onClose
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setScale(1);
      setRotation(0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrev();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length]);

  const handlePrev = useCallback(() => {
    if (images.length <= 1 || isAnimating) return;
    setIsAnimating(true);
    setScale(1);
    setRotation(0);
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 300);
  }, [images.length, isAnimating]);

  const handleNext = useCallback(() => {
    if (images.length <= 1 || isAnimating) return;
    setIsAnimating(true);
    setScale(1);
    setRotation(0);
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 300);
  }, [images.length, isAnimating]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleResetZoom = () => {
    setScale(1);
    setRotation(0);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div className="image-modal-overlay" onClick={handleBackdropClick}>
      <div className="image-modal-container">
        {/* Close button */}
        <button className="image-modal-close" onClick={onClose} title="Close (Esc)">
          <X size={24} />
        </button>

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              className="image-modal-nav image-modal-prev"
              onClick={handlePrev}
              title="Previous (Left Arrow)"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              className="image-modal-nav image-modal-next"
              onClick={handleNext}
              title="Next (Right Arrow)"
            >
              <ChevronRight size={32} />
            </button>
          </>
        )}

        {/* Image container */}
        <div className="image-modal-content">
          <img
            src={currentImage?.src || currentImage}
            alt={currentImage?.name || `Image ${currentIndex + 1}`}
            className={`image-modal-image ${isAnimating ? 'animating' : ''}`}
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
            }}
            draggable={false}
          />
        </div>

        {/* Controls */}
        <div className="image-modal-controls">
          <button onClick={handleZoomOut} title="Zoom Out (-)" disabled={scale <= 0.5}>
            <ZoomOut size={20} />
          </button>
          <span className="zoom-level">{Math.round(scale * 100)}%</span>
          <button onClick={handleZoomIn} title="Zoom In (+)" disabled={scale >= 3}>
            <ZoomIn size={20} />
          </button>
          <div className="control-divider" />
          <button onClick={handleRotate} title="Rotate">
            <RotateCw size={20} />
          </button>
          <button onClick={handleResetZoom} className="reset-btn" title="Reset">
            Reset
          </button>
        </div>

        {/* Image counter */}
        {images.length > 1 && (
          <div className="image-modal-counter">
            {currentIndex + 1} / {images.length}
          </div>
        )}

        {/* Thumbnail strip for multiple images */}
        {images.length > 1 && (
          <div className="image-modal-thumbnails">
            {images.map((img, idx) => (
              <button
                key={idx}
                className={`thumbnail ${idx === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  setCurrentIndex(idx);
                  setScale(1);
                  setRotation(0);
                }}
              >
                <img src={img?.src || img} alt={`Thumbnail ${idx + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagePreviewModal;
